
<template>
  <div>
    <LoadingIndicator v-if="negotiationsLoading" centered />
    <div v-else>
      <b-table
        v-if="filteredOffers.length"
        :items="filteredOffers"
        :fields="fields"
        responsive
        :tbody-tr-class="rowClass"
        @row-clicked="toggleRow"
      >
        <template #table-colgroup>
          <col style="width: 70px">
          <col style="width: auto">
          <col style="width: 75px">
          <col style="width: auto">
          <col style="width: 104px">
          <col style="width: 90px">
          <col style="width: 90px">
          <col style="width: auto">
          <col style="width: 110px">
          <col style="width: auto">
          <col style="width: 66px">
          <col style="width: auto">
        </template>


        <!-- From -->
        <template #cell(from)="data">
          <div class="d-flex align-items-center" style="gap:4px;">
            <BaseIcon :name="getLoadboardOption(data.item.loadboard).iconname" :color="IconBaseColor" size="24" />
            <span style="font-size: 14px;">{{getLoadboardOption(data.item.loadboard).name}}</span>
          </div>
        </template>

        <!-- Carrier -->
        <template #cell(carrier)="data">
          <b-link v-if="data.item.carrier" :to="carrierLink(data.item.carrier)" @click.stop>
            {{ data.item.carrier.name }}
          </b-link>
        </template>

        <!-- Trust Score -->
        <template #cell(trustScore)="data">
          <div class="d-flex align-items-center justify-content-center">
            <div v-if="data.item.carrier" class="trustScore" :class="data.isFlagged ? 'trustScoreFlagged' : 'trustScoreNotFlagged'">
              {{ data.item.carrier.trustScore || 'NA' }}
            </div>
          </div>
        </template>
        <!-- Pickup & Delivery -->
        <template #cell(pickupDelivery)="data">
          <div>
            <div class="d-flex align-items-center" style="gap:4px;">
              <BaseIcon name="location" :color="IconBaseColor" size="12" />
              <span :class="{ 'text-red': data.item.pickupDateType && data.item.pickupDateType.id !== order.pickupDateTypeId }">
                {{ dateTypeLabel(data.item.pickupDateType && data.item.pickupDateType.id) }}
              </span>
              <span :class="{ 'text-red': data.item.pickupDate !== order.pickupDate }">
                {{ data.item.pickupDate | shortFormatDate }}
              </span>
            </div>
            <div class="d-flex align-items-center" style="gap:4px;">
              <BaseIcon name="locations" :color="IconBaseColor" size="12" />
              <span :class="{ 'text-red': data.item.deliveryDateType && data.item.deliveryDateType.id !== order.deliveryDateTypeId }">
                {{ dateTypeLabel(data.item.deliveryDateType && data.item.deliveryDateType.id) }}
              </span>
              <span :class="{ 'text-red': data.item.deliveryDate !== order.deliveryDate }">
                {{ data.item.deliveryDate | shortFormatDate }}
              </span>
            </div>
          </div>
        </template>


        <!-- Transport -->
        <template #cell(transportType)="data">
          <span :class="{ 'text-red': data.item.transportType && data.item.transportType.id !== order.transportTypeId }">
            {{data.item.transportType ? data.item.transportType.title : '-'}}
          </span>
        </template>


        <!-- They Requested -->
        <template #cell(theyRequested)="data">
          <span v-if="data.item.carrierOffer" :class="{ 'text-red': data.item.carrierOffer !== order.carrierPay }">
            {{ data.item.carrierOffer | formatMoney }}
          </span>
          <span v-else>-</span>
        </template>

        <!-- We Offered -->
        <template #cell(weOffered)="data">
          <div v-if="data.item.nexusOffer" class="flex flex-col">
            <div :class="{ 'text-red': data.item.nexusOffer.amount !== order.carrierPay }">{{ data.item.nexusOffer.amount | formatMoney }}</div>
            <div>by {{ data.item.nexusOffer.by }}</div>
          </div>
          <span v-else>-</span>
        </template>

        <!-- Notes -->
        <template #cell(note)="data">
          <div class="d-flex align-items-center justify-content-center">
            <BaseIcon v-if="data.item.note" name="check-circle-filled" :color="IconNoteColor" size="20" />
          </div>
        </template>

        <!-- Status -->
        <template #cell(status)="data">
          <div class="statusBadge" :style="statusStyle(data.item.offerStatus)">
            {{ data.item.offerStatus | capitalize }}
          </div>
        </template>
 
        <!-- Expiry -->
        <template #cell(expiry)="data">
          <span v-if="data.item.expiresAt">{{ data.item.expiresAt | timeUntil }}</span>
          <span v-else>-</span>
        </template>

        <!-- Actions -->
        <template #cell(actions)="data">
          <b-btn v-if="canManageOffers" size="sm" variant="primary" @click.stop="handleDispatching(order, data.item)">
            Dispatch
          </b-btn>
        </template>

        <!-- Expand Icon -->
        <template #cell(expand)="{ detailsShowing, toggleDetails }">
          <div class="expandButton d-flex align-items-center justify-content-center" @click.stop="toggleDetails">
            <i :class="detailsShowing ? 'ion ion-ios-remove' : 'ion ion-ios-arrow-down'"  />
          </div>
        </template>

        <!-- ROW DETAILS -->
        <template #row-details="{ item }">
          <div class="nd-wrapper">
            <div class="d-flex justify-content-between align-items-center nd-header">
              <div class="nd-title">
                Negotiation History ({{ item.negotiationHistory ? item.negotiationHistory.length : 0 }})
              </div>
              <!-- Row actions-->
              <div v-if="canManageOffers" class="nd-actions">
                <b-btn v-if="canCounterOffer(item)" class="ng-btn" size="sm" @click.stop="handleCounterOffer(item)">
                  <i class="fa fa-dollar-sign" />
                  Counteroffer
                </b-btn>
                <b-btn v-if="canWithdraw(item)" class="ng-btn" size="sm" @click.stop="handleWithdraw(item)">
                  Withdraw
                </b-btn>
                <b-btn v-if="canDecline(item)" class="ng-btn" size="sm" @click.stop="handleDeclineOffer(item)">
                  <i class="far fa-trash-alt" />
                  Decline
                </b-btn>
                <b-btn v-if="canAccept(item)" class="ng-btn" size="sm" @click.stop="handleAcceptOffer(item)">
                  Accept Offer
                </b-btn>
                <b-btn size="sm" variant="primary" @click.stop="handleDispatching(order, item)">
                  Dispatch
                </b-btn>
              </div>
            </div>
            <div v-for="neg in [...item.negotiationHistory].reverse()" :key="neg.id" style="display: flex; gap: 20px; white-space: nowrap;">
              <div><b>{{ neg.carrierPay | formatMoney }}</b> by {{ neg.by }}</div>
              <div><span v-if="neg.pickupDateType">{{ neg.pickupDateType }}</span><span v-else> Pickup Date </span>{{ neg.pickupDate | shortFormatDate }}<span v-if="neg.transportType">, {{ neg.transportType }}</span></div>
              <div class="text-muted"><span v-if="!neg.declinedAt">Created at {{ neg.createdAt | formatDateTime }}</span><span v-else>Declined at {{ neg.declinedAt | formatDateTime }}</span></div>
            </div>
          </div>
        </template>
      </b-table>
      <div class="alert alert-warning" v-else>No truckers have been added</div>
    </div>


    <DispatchModal v-if="dispatching" :order="dispatchingOrder" :offer="dispatchingOffer"
      @refresh="$emit('refresh')" :on-close="() => (dispatching = null)" />

    <CounterOfferModal v-if="counterOffering" :order="order" :negotiation="counterOfferNegotiation"
      @refresh="() => { $emit('refresh'); fetchNegotiations(); }" :on-close="() => (counterOffering = false)" />
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import LoadingIndicator from './LoadingIndicator.vue';
import DispatchModal from './DispatchModal.vue';
import CounterOfferModal from './CounterOfferModal.vue';
import dateTypesMixin from '@/mixins/dateTypes';
import transportTypesMixin from '@/mixins/transportTypes';
import BaseIcon from '@/components/shared/BaseIcon.vue';

const OFFER_TYPE = {
  INCOMING: 'incoming',
  ACCEPTED: 'accepted',
};

const OFFER_STATUS = {
  WITHDRAWN: 'withdrawn',
  DECLINED: 'declined',
};

export default {
  name: 'OrdersOffers',
  components: { LoadingIndicator, DispatchModal, CounterOfferModal, BaseIcon },
  mixins: [dateTypesMixin, transportTypesMixin],
  props: {
    order: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      default: null,
    },
    refresh: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      negotiations: [],
      negotiationsLoading: true,
      dispatching: false,
      dispatchingOrder: null,
      dispatchingOffer: null,
      counterOffering: false,
      counterOfferNegotiation: null,
      fields: [
        { key: 'from', label: 'From' },
        { key: 'carrier', label: 'Carrier' },
        { key: 'trustScore', label: 'Trust Score', thClass: 'text-center' },
        { key: 'pickupDelivery', label: 'Pickup & Delivery' },
        { key: 'transportType', label: 'Transport' },
        { key: 'theyRequested', label: 'They Requested' },
        { key: 'weOffered', label: 'We Offered' },
        { key: 'note', label: 'Notes', thClass: 'text-center' },
        { key: 'status', label: 'Status' },
        { key: 'expiry', label: 'Expiry' },
        { key: 'actions', label: '' },
        { key: 'expand', label: '' },
      ],
      statusOptions: [
        { text: 'active',     textColor: '#131415', bgColor: '#FFAC71', darkTextColor: '#FFAC71', darkBgColor: '#79421B'},
        { text: 'pending',    textColor: '#131415', bgColor: '#FFD950', darkTextColor: '#FFDF6D', darkBgColor: '#473D25'},
        { text: 'queued',     textColor: '#131415', bgColor: '#93E7EF', darkTextColor: '#FFAC71', darkBgColor: '#79421B'},
        { text: 'declined',   textColor: 'white',   bgColor: '#52565B', darkTextColor: '#B3B4B4', darkBgColor: '#494B51'},
        { text: 'withdrawn',  textColor: 'white',   bgColor: '#52565B', darkTextColor: '#B3B4B4', darkBgColor: '#494B51'},
        { text: 'expired',    textColor: 'white',   bgColor: '#52565B', darkTextColor: '#B3B4B4', darkBgColor: '#494B51'},
        { text: 'dispatched', textColor: 'white',   bgColor: '#125C36', darkTextColor: '#95D596', darkBgColor: '#224332'},
        { text: 'accepted',   textColor: 'white',   bgColor: '#125C36', darkTextColor: '#95D596', darkBgColor: '#224332'},
      ],
      loadboardOptions: [
        { id: null, iconname: 'crm-icon', name:'CRM'},
        { id: 1,    iconname: 'cd-icon',  name:'CD'},
        { id: 2,    iconname: 'hcl-icon', name:'HCL'},
        { id: 3,    iconname: 'sd-icon',  name:'SD'},
      ],
    };
  },
  computed: {
    filteredOffers() {
      if (!this.status || this.status === 'all') return this.negotiations;
      return this.negotiations.filter(o => o.offerStatus === this.status);
    },
    canManageOffers() {
      // check if the order is already dispatched, if it is, the user should not be able to manage offers
      return !this.order.carrier;
    },
     IconBaseColor() {
      return localStorage.getItem('themeSettingsStyle') === 'dark' ? '#FFFFFF' : '#4E5155';
    },
     IconNoteColor() {
      return localStorage.getItem('themeSettingsStyle') === 'dark' ? '#B1B8C0' : '#6D737A';
    },
  },
  created() {
    this.fetchNegotiations();
  },
  methods: {
    toggleRow(item) {
      this.$set(item, '_showDetails', !item._showDetails);
    },
    
    /** Roles for action button visibility **/
    // These methods determine whether certain action buttons should be visible based on the offer's status and type.
    isOpenOffer(item) {
      return item.offerType !== OFFER_TYPE.ACCEPTED;
    },
    canCounterOffer(item) {
      return this.isOpenOffer(item);
    },
    canWithdraw(item) {
      return this.isOpenOffer(item)
        && !!item.nexusOffer
        && item.offerStatus !== OFFER_STATUS.WITHDRAWN;
    },
    canDecline(item) {
      return this.isOpenOffer(item)
        && !item.nexusOffer
        && item.offerStatus !== OFFER_STATUS.DECLINED;
    },
    canAccept(item) {
      return item.offerType === OFFER_TYPE.INCOMING;
    },
    /** End of visibility rules **/


    handleDispatching(order, offer) {
      this.dispatching = true;
      this.dispatchingOrder = order;
      // DispatchModal expects offer.carrierId and offer.carrier.contact.company
      this.dispatchingOffer = {
        ...offer,
        carrierId: offer.carrier ? offer.carrier.id : null,
        carrier: offer.carrier ? {
          ...offer.carrier,
          contact: offer.carrier.contact || { company: offer.carrier.name },
        } : null,
      };
    },
    async dispatchOrder(carrierId) {
      const response = await ApiClient.leads.dispatch.post({
        leadId: this.order.id,
        body: { carrierId: carrierId },
      });
      if (response.status === 200) {
        this.errors = {};
        this.$emit('refresh');
      } else if (response.status === 400) {
        this.errors = { generalError: response.data.message };
        
      } else if (response.status === 422) {
        this.errors = response.data.errors;
      }
    },
    async handleAcceptOffer(negotiation) {
      if (this.isOfferExpired(negotiation.latestCarrierOffer)) {
        this.$bvToast.toast('This offer has expired and cannot be accepted.', {
          title: 'Offer Expired',
          variant: 'warning',
          solid: true,
        });
        return;
      }

      const response = await ApiClient.leads.offers.accept({
        leadId: this.order.id,
        id: negotiation.id,
      });

      if (response.status === 200) {
        this.$bvToast.toast('Offer accepted successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.$emit('refresh');
        this.fetchNegotiations();
      } else if (response.status === 400) {
        this.$bvToast.toast(response.data.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } else {
        this.$bvToast.toast('An error occurred. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    },
    handleCounterOffer(negotiation) {
      if (this.isOfferExpired(negotiation.latestCarrierOffer)) {
        this.$bvToast.toast('This offer has expired and cannot be countered.', {
          title: 'Offer Expired',
          variant: 'warning',
          solid: true,
        });
        return;
      }
      this.counterOffering = true;
      this.counterOfferNegotiation = {
        ...negotiation,
        carrier: negotiation.carrier ? {
          ...negotiation.carrier,
          company: negotiation.carrier.company || negotiation.carrier.name,
        } : negotiation.carrier,
      };
    },
    async handleWithdraw(negotiation) {
      const response = await ApiClient.leads.offers.withdraw({
        leadId: this.order.id,
        id: negotiation.id,
      });

      if (response.status === 200) {
        this.$bvToast.toast('Offer withdrawn.', { title: 'Success', variant: 'info', solid: true });
        this.fetchNegotiations();
      } else {
        this.$bvToast.toast(response.data?.message || 'An error occurred.', { title: 'Error', variant: 'danger', solid: true });
      }
    },
    async handleDeclineOffer(negotiation) {
      const response = await ApiClient.leads.offers.decline({
        leadId: this.order.id,
        id: negotiation.id,
      });
      console.log(response);

      if (response.status === 200) {
        this.$bvToast.toast('Offer declined successfully.', { title: 'Success', variant: 'info', solid: true });
        this.fetchNegotiations();
      } else {
        this.$bvToast.toast(response.data?.message || 'An error occurred.', { title: 'Error', variant: 'danger', solid: true });
      }
    },
    async fetchNegotiations() {
      this.negotiationsLoading = true;
      try {
        const response = await ApiClient.leads.negotiations.get({ leadId: this.order.id });
        if (response.status === 200) {
          this.negotiations = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching negotiations:', error);
      }
      this.negotiationsLoading = false;
    },
    isOfferExpired(offer) {
      if (!offer || !offer.expiresAt) return false;
      return new Date(offer.expiresAt) < new Date();
    },
    getLoadboardOption(loadboard) {
      const id = loadboard ? loadboard.id : null;
      return this.loadboardOptions.find(o => o.id === id) || this.loadboardOptions[0];
    },
    rowClass(item) {
      if (item.deletedAt) return 'text-secondary';
      if (item.offerStatus === 'dispatched') return 'row-dispatched';
      return '';
    },
    carrierLink(carrier) {
      return carrier ? { name: 'CarrierDetails', params: { id: carrier.id } } : null;
    },
    statusStyle(status) {
      const option = this.statusOptions.find(o => o.text === status);
      if (!option) return {};
      const dark = localStorage.getItem('themeSettingsStyle') === 'dark';
      return { color: dark ? option.darkTextColor : option.textColor, backgroundColor: dark ? option.darkBgColor : option.bgColor };
    },
  },
  watch: {
    refresh(val) {
      if (val) this.fetchNegotiations();
    },
  },
};
</script>

<style lang="scss">
.statusBadge {
  width: 100px;
  border-radius: 100px;
  padding: 4px;
  text-align: center;
}
.text-red {
  color: #BC1313;
}
.dark-style .text-red {
  color: #D9534F;
}
/*Buttons*/
.secondary-btn{
  background-color: #F5F5F5;
  color: #4E5155;
}
.dark-style .secondary-btn{
  background-color: #656871;
  color: #FFFFFF;
}
/* Trust Score */
.trustScore {
  height: 32px;
  width: 32px;
  border-radius: 100px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.trustScoreFlagged{
  color: #BC1313;
  background-color: #FFEEEA;
}
.dark-style .trustScoreFlagged{
  color: #D9534F;
  background-color: #4A2D2C;
}
.trustScoreNotFlagged{
  color: #4E5155;
  background-color: #F5F5F5;
}
.dark-style .trustScoreNotFlagged{
  color: #FFFFFF;
  background-color: #505256;
}
/* Negotiation History  */
.nd-wrapper {
  background: #F5F5F5;
  padding: 8px 1px 16px 74px;
  border-top: 1px solid #F1F1F2;
}
.dark-style .nd-wrapper {
  background: #393B40;
  border-bottom: 1px solid #45474D;
  border-top: 1px solid #45474D;
}
.nd-header {
  padding: 8px 50px 8px 0;
}
.nd-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.nd-title {
  font-size: 14px;
  font-weight: 600;
  color: #2f3a4c;
}
.dark-style .nd-title {
  color: #FFFFFF;
}
.ng-btn{
  background-color: #FFFFFF;
  color: #4E5155;
  gap: 4px;
}
.dark-style .ng-btn{
  background-color: #656871;
  color: #FFFFFF;
}
.expandButton {
  width: 28px;
  height: 26px;
  border-radius: 4px;
  background-color: #E8E8E8;
  flex-shrink: 0;
}
.dark-style .expandButton{
  background-color: #51555E;
}
/* Dispatched row highlight */
.row-dispatched {
  background-color: #EFF6EC !important;
}
.dark-style .row-dispatched {
  background-color: #2B3831 !important;
}
/* Ion Icons */
.ion{
  color: #131415;
}
.dark-style .ion{
  color: white;
}
</style>
